import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactPlayer from 'react-player';
import { Helmet } from 'react-helmet-async';
import {
  FacebookIcon,
  FacebookShareButton,
  TwitterShareButton,
  XIcon,
  WhatsappShareButton,
  WhatsappIcon,
} from 'react-share';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';

import {
  useSearchShowQuery,
  useGetWatchListQuery,
  useAddListMutation,
  useDeleteMovieMutation,
} from '../actions/apiSlice';
import { useGetRecommendationsQuery, useSearchCreditsQuery } from '../actions/tmdbApiSlice';
import { useLazySearchTrailersQuery } from '../actions/searchTrailer';
import useRedirectToLogin from '../hooks/useRedirectToLogin';
import EpisodeAvailability from '../components/EpisodeAvailability';
import FunFacts from '../components/FunFacts';
import MediaRail from '../components/MediaRail';
import PosterCard from '../components/PosterCard';

const placeholderImageUrl = 'https://placehold.co/300x450.png?text=No+Poster';

const ShowDetailsScreen = () => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q');
  const mid = searchParams.get('mid');
  const tid = searchParams.get('tid');
  const imdb = searchParams.get('imdb');

  const { data: show, error, isFetching } = useSearchShowQuery({
    q: searchQuery,
    mid,
    tid,
    imdb,
  });

  // Cast & recommendations need a TMDB id + type. Prefer the URL params; fall
  // back to what the backend resolved (e.g. items navigated by IMDb id from the
  // TVmaze "Top picks" slider, or by plain title) so they load in every case.
  const creditsType = mid ? 'movie' : tid ? 'tv' : show?.media_type || null;
  const creditsId = mid || tid || show?.tmdb_id || null;
  const credMid = creditsType === 'movie' ? creditsId : null;
  const credTid = creditsType === 'tv' ? creditsId : null;
  const creditsSkip = !creditsId;
  const { data: recommendData } = useGetRecommendationsQuery(
    { mid: credMid, tid: credTid },
    { skip: creditsSkip },
  );
  const { data: personData } = useSearchCreditsQuery(
    { mid: credMid, tid: credTid },
    { skip: creditsSkip },
  );

  const [triggerSearchTrailers, { data: trailerData, isLoading: trailerLoading, error: trailerError }] =
    useLazySearchTrailersQuery();

  const [addToList, { isLoading: addListLoading, isError: addListError, error: listErrorData }] =
    useAddListMutation();
  const [deleteFromList, { isLoading: deleteListLoading }] = useDeleteMovieMutation();
  const redirectToLogin = useRedirectToLogin();
  const token = localStorage.getItem('aircheck_access');

  const { data: watchList, isFetching: listFetching } = useGetWatchListQuery(undefined, {
    skip: !token,
  });

  const navigate = useNavigate();
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [watchlistEntryId, setWatchlistEntryId] = useState(null);
  const [resetForm, setResetForm] = useState(false);
  const [videos, setVideos] = useState([]);
  const [trailerLoaded, setTrailerLoaded] = useState(false);
  const [tab, setTab] = useState(0);

  const recommendList = recommendData?.results;

  useEffect(() => {
    if (trailerData) setVideos(trailerData.items);
  }, [trailerData]);

  useEffect(() => {
    setResetForm(true);
    setTrailerLoaded(false);
    setTab(0);
    setVideos([]);
    const t = setTimeout(() => setResetForm(false), 0);
    return () => clearTimeout(t);
  }, [searchQuery, mid, tid, imdb]);

  useEffect(() => {
    if (show && show.kind && watchList && token) {
      const entry = watchList.find((item) => item.title_id === show.imdbID);
      setIsInWatchlist(Boolean(entry));
      setWatchlistEntryId(entry ? entry.id : null);
    }
  }, [show, watchList, token]);

  useEffect(() => {
    if (addListError && listErrorData?.data) {
      toast.error(`${Object.values(listErrorData.data)}`);
    }
  }, [addListError, listErrorData]);

  const currentUrl = window.location.href;

  const handleRecommendedItemNavigate = (item) => {
    let year = '';
    if (item.media_type === 'movie' && item.release_date) {
      year = item.release_date.split('-')[0];
    } else if (item.media_type === 'tv' && item.first_air_date) {
      year = item.first_air_date.split('-')[0];
    }
    const titleWithYear = year ? `${item.title || item.name} ` : item.title || item.name;
    const idParam = item.media_type === 'tv' ? 'tid' : 'mid';
    navigate(`/details/?q=${titleWithYear}&${idParam}=${item.id}`);
  };

  const handlePersonNavigation = (name, director = false) => {
    navigate(director ? `/person/?q=${name}&dir=true` : `/person/?q=${name}`);
  };

  const processMovieTitle = (movie) => {
    return movie.title
      .split(' ')
      .map((w) => w.replace(/[^a-zA-Z0-9]/g, ''))
      .join('-');
  };

  const convertMinutesToHoursAndMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    return `${hours}h ${remaining}m`;
  };

  const removeFromWatchlist = useCallback(async () => {
    if (!watchlistEntryId) return;
    try {
      await deleteFromList(watchlistEntryId).unwrap();
      toast.success('Removed from watchlist');
    } catch (err) {
      toast.error('Could not remove from watchlist');
    }
  }, [watchlistEntryId, deleteFromList]);

  const setWatchlist = useCallback(async () => {
    if (isInWatchlist) {
      removeFromWatchlist();
      return;
    }
    if (!show) return;
    const localToken = localStorage.getItem('aircheck_access');

    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      const parts = dateStr.split(' ');
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const months = {
          Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
          Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12',
        };
        return [parts[2], months[parts[1]], day].join('-');
      }
      return dateStr;
    };

    if (!localToken) {
      toast.error('Please log in');
      redirectToLogin();
      return;
    }

    const isMovie = show.kind.includes('movie');
    const payload = {
      name: show.title,
      title_id: show.imdbID,
      year: show.year,
      full_poster_url: show['full-size cover url'],
      small_poster_url: show['cover url'],
      air_date: isMovie && show['original air date'] ? formatDate(show['original air date']) : null,
      is_movie: isMovie,
      // Prefer the TMDB id the backend resolved (covers movies and IMDb/title
      // navigations); fall back to the tv id from the URL.
      tmdb_id: show.tmdb_id || (tid ? Number(tid) : null),
    };

    addToList(payload);
  }, [isInWatchlist, show, redirectToLogin, addToList, tid, removeFromWatchlist]);

  const handleTrailerLoad = () => {
    if (!trailerLoaded && show) {
      triggerSearchTrailers({ query: show.title, year: show.year, kind: show.kind });
      setTrailerLoaded(true);
    }
  };

  if (isFetching) {
    return (
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
          <Skeleton variant="rounded" sx={{ width: { xs: '100%', md: 280 }, height: 420 }} />
          <Box sx={{ flex: 1, width: '100%' }}>
            <Skeleton variant="text" height={60} width="60%" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="40%" />
          </Box>
        </Stack>
      </Container>
    );
  }

  if (error) return <Container sx={{ py: 6 }}>Error: {error.message}</Container>;
  if (!show || !show.kind) return null;

  const backdrop = show['full-size cover url'];
  const isMovie = show.kind.includes('movie');
  const isSeries = show.kind.includes('series') && !show.kind.includes('podcast');

  // Structured data so search engines can surface rich results (air dates,
  // ratings, cast) for this title. Built from whatever fields are present.
  const ogImage = show['cover url'] || backdrop || placeholderImageUrl;
  const metaTitle = `${show.title}${show.year ? ` (${show.year})` : ''} | Air dates, cast & where to watch | Aircheck`;
  const metaDescription =
    show['plot outline'] ||
    `Air dates, cast, trailers and reminders for ${show.title}${show.year ? ` (${show.year})` : ''}.`;
  const directorNames = (show.directors || [])
    .flat()
    .map((d) => (typeof d === 'object' ? d.name : d))
    .filter(Boolean);
  const castNames = (personData?.cast || show.cast || [])
    .slice(0, 10)
    .map((m) => m.name)
    .filter(Boolean);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': isSeries ? 'TVSeries' : 'Movie',
    name: show.title,
    url: currentUrl,
    ...(metaDescription ? { description: metaDescription } : {}),
    ...(ogImage ? { image: ogImage } : {}),
    ...(show.genres && show.genres.length ? { genre: show.genres } : {}),
    ...(show.year ? { datePublished: String(show.year) } : {}),
    ...(directorNames.length
      ? { director: directorNames.map((name) => ({ '@type': 'Person', name })) }
      : {}),
    ...(castNames.length
      ? { actor: castNames.map((name) => ({ '@type': 'Person', name })) }
      : {}),
    ...(show.rating
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: show.rating,
            bestRating: 10,
          },
        }
      : {}),
  };
  const watchUrl = isMovie && token
    ? `https://lookmovie2.to/movies/view/${show.imdbID}-${processMovieTitle(show)}-${show.year}`
    : isSeries && token
    ? `https://lookmovie2.to/shows/view/${show.imdbID}-${processMovieTitle(show)}-${show.year}`
    : '';

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={currentUrl} />

        <meta property="og:type" content={isSeries ? 'video.tv_show' : 'video.movie'} />
        <meta property="og:site_name" content="Aircheck" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={currentUrl} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <Box sx={{ position: 'relative', width: '100%', minHeight: { xs: 'auto', md: 560 }, mb: 4 }}>
        {backdrop && (
          <Box
            component="img"
            src={backdrop}
            alt={show.title}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'blur(20px) saturate(1.2)',
              opacity: 0.45,
              transform: 'scale(1.1)',
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: (t) =>
              t.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(10,10,15,0.7) 0%, rgba(10,10,15,0.85) 70%, rgba(10,10,15,1) 100%)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(250,250,251,0.9) 70%, rgba(250,250,251,1) 100%)',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', pt: { xs: 3, md: 6 }, pb: 5 }}>
          <Grid container spacing={4} alignItems="flex-end">
            <Grid item xs={12} md={3}>
              <Box
                component={watchUrl ? 'a' : 'div'}
                href={watchUrl || undefined}
                target={watchUrl ? '_blank' : undefined}
                rel={watchUrl ? 'noopener noreferrer' : undefined}
                sx={{
                  display: 'block',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                  aspectRatio: '2 / 3',
                  bgcolor: 'background.paper',
                  cursor: watchUrl ? 'pointer' : 'default',
                }}
              >
                <Box
                  component="img"
                  src={show['cover url'] || placeholderImageUrl}
                  alt={show.title}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={9}>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                <Chip
                  size="small"
                  color="primary"
                  label={isMovie ? 'Movie' : isSeries ? 'Series' : show.kind}
                />
                {show.rating && (
                  <Chip
                    size="small"
                    icon={<StarIcon sx={{ fontSize: 16 }} />}
                    label={show.rating}
                  />
                )}
                {show.year && (
                  <Chip
                    size="small"
                    icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
                    label={show.year}
                  />
                )}
                {show.runtimes && (
                  <Chip
                    size="small"
                    icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                    label={convertMinutesToHoursAndMinutes(show.runtimes)}
                  />
                )}
              </Stack>

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: 32, sm: 44, md: 56 },
                  fontWeight: 900,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.05,
                  mb: 2,
                }}
              >
                {show.title}
              </Typography>

              {show.genres && (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                  {show.genres.map((g) => (
                    <Chip key={g} size="small" variant="outlined" label={g} />
                  ))}
                </Stack>
              )}

              <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                {watchUrl && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<PlayArrowIcon />}
                    href={watchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Watch now
                  </Button>
                )}
                {(isMovie || isSeries) && (
                  <Tooltip
                    title={isInWatchlist ? 'Click to remove from watchlist' : 'Add to watchlist'}
                  >
                    <Button
                      variant={isInWatchlist ? 'contained' : 'outlined'}
                      color={isInWatchlist ? 'success' : 'inherit'}
                      size="large"
                      disabled={addListLoading || listFetching || deleteListLoading}
                      startIcon={
                        addListLoading || listFetching || deleteListLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : isInWatchlist ? (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              '& .hover-icon': { display: 'none' },
                              '.watchlist-btn:hover &': {
                                '& .default-icon': { display: 'none' },
                                '& .hover-icon': { display: 'inline-flex' },
                              },
                            }}
                          >
                            <CheckCircleOutlineIcon className="default-icon" />
                            <RemoveCircleOutlineOutlinedIcon className="hover-icon" />
                          </Box>
                        ) : (
                          <ControlPointOutlinedIcon />
                        )
                      }
                      onClick={setWatchlist}
                      className="watchlist-btn"
                      sx={{
                        borderColor: (t) => t.palette.divider,
                        '&:hover': isInWatchlist
                          ? { bgcolor: 'error.main', color: '#fff' }
                          : undefined,
                        '& .watchlist-label-default': { display: 'inline' },
                        '& .watchlist-label-hover': { display: 'none' },
                        '&:hover .watchlist-label-default': { display: 'none' },
                        '&:hover .watchlist-label-hover': { display: 'inline' },
                      }}
                    >
                      {isInWatchlist ? (
                        <>
                          <Box component="span" className="watchlist-label-default">
                            In watchlist
                          </Box>
                          <Box component="span" className="watchlist-label-hover">
                            Remove
                          </Box>
                        </>
                      ) : (
                        'Add to watchlist'
                      )}
                    </Button>
                  </Tooltip>
                )}
                {watchUrl && (
                  <Button
                    variant="text"
                    color="inherit"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    href={watchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open external
                  </Button>
                )}
              </Stack>

              {isMovie && show.directors && show.directors.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Director:
                  </Box>{' '}
                  {show.directors.flat().map((dir, idx, arr) => {
                    const name = typeof dir === 'object' ? dir.name : dir;
                    return (
                      <Box
                        key={idx}
                        component="span"
                        onClick={() => handlePersonNavigation(name, true)}
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          mr: idx < arr.length - 1 ? 0.5 : 0,
                          '&:hover': { color: 'primary.main' },
                        }}
                      >
                        {name}
                        {idx < arr.length - 1 ? ',' : ''}
                      </Box>
                    );
                  })}
                </Typography>
              )}

              {isSeries && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Seasons:
                  </Box>{' '}
                  {show.seasons} · <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Years:
                  </Box>{' '}
                  {show['series years']}
                </Typography>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <FacebookShareButton url={currentUrl}>
                  <FacebookIcon size={36} round />
                </FacebookShareButton>
                <WhatsappShareButton url={currentUrl}>
                  <WhatsappIcon size={36} round />
                </WhatsappShareButton>
                <TwitterShareButton url={currentUrl}>
                  <XIcon size={36} round />
                </TwitterShareButton>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 8 }}>
        {/* Plot */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3.5 },
            mb: 4,
            borderRadius: 3,
            border: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Overview
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {show['plot outline']}
          </Typography>
        </Paper>

        {/* Top billed cast */}
        {personData?.cast && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Top billed cast
            </Typography>
            <Box
              className="scroll-rail"
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pb: 1,
              }}
            >
              {personData.cast.slice(0, 12).map((member) => (
                <Box
                  key={member.id}
                  onClick={() => handlePersonNavigation(member.name)}
                  sx={{
                    flex: '0 0 auto',
                    width: 130,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover .cast-avatar': { transform: 'scale(1.05)' },
                  }}
                >
                  <Avatar
                    className="cast-avatar"
                    src={
                      member.profile_path
                        ? `https://image.tmdb.org/t/p/w300${member.profile_path}`
                        : undefined
                    }
                    alt={member.name}
                    sx={{
                      width: 110,
                      height: 110,
                      mx: 'auto',
                      mb: 1,
                      transition: 'transform .25s ease',
                      border: (t) => `2px solid ${t.palette.divider}`,
                    }}
                  />
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {member.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap component="div">
                    {member.job || member.character}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Fallback cast from show */}
        {!personData && show.cast && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
              Cast
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {show.cast.slice(0, 8).map((member) => (
                <Chip
                  key={member.name}
                  label={member.name}
                  onClick={() => handlePersonNavigation(member.name)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Tabs: Trailer / Fun facts / Episode check */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: (t) => `1px solid ${t.palette.divider}`,
            overflow: 'hidden',
            mb: 4,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v);
              if (v === 0) handleTrailerLoad();
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}`, px: 2 }}
          >
            <Tab label="Trailer" />
            <Tab label="Fun facts" />
            {isSeries && <Tab label="Episode availability" />}
          </Tabs>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {tab === 0 && (
              <Box>
                {trailerLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                  </Box>
                ) : videos && videos.length > 0 ? (
                  videos.slice(0, 1).map((video) => (
                    <Box className="player-wrapper" key={video.id.videoId}>
                      <ReactPlayer
                        className="react-player"
                        url={`https://www.youtube.com/watch?v=${video.id.videoId}`}
                        width="100%"
                        height="100%"
                        controls
                      />
                    </Box>
                  ))
                ) : (
                  <Typography color="text.secondary">
                    {trailerError ? `Error: ${trailerError.message}` : 'Click the tab again to load the trailer.'}
                  </Typography>
                )}
              </Box>
            )}

            {tab === 1 && <FunFacts show={show} />}

            {tab === 2 && isSeries && <EpisodeAvailability show={show} resetForm={resetForm} />}
          </Box>
        </Paper>

        {/* Recommendations */}
        {recommendList && recommendList.length > 0 && (
          <MediaRail title="You may also like">
            {recommendList.map((item) => (
              <PosterCard
                key={item.id}
                src={
                  item.poster_path
                    ? `https://image.tmdb.org/t/p/w342${item.poster_path}`
                    : placeholderImageUrl
                }
                title={item.title || item.name}
                subtitle={item.release_date || item.first_air_date || ''}
                badge={
                  item.vote_average
                    ? `★ ${(Math.floor(item.vote_average * 10) / 10).toFixed(1)}`
                    : undefined
                }
                onClick={() => handleRecommendedItemNavigate(item)}
              />
            ))}
          </MediaRail>
        )}

        <Divider sx={{ my: 4 }} />
      </Container>
    </>
  );
};

export default ShowDetailsScreen;
